"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Container from "@/components/ui/container";
import { siteContent } from "@/lib/data";
import { cn } from "@/lib/cn";

const AUTOPLAY_MS = 6000;

export default function HeroBanner() {
  const slides = siteContent.heroSlides;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next: number) => setIndex(((next % slides.length) + slides.length) % slides.length),
    [slides.length],
  );
  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => next(), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, paused, next]);

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Featured"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative"
    >
      <div className="relative h-[28rem] sm:h-[32rem] lg:h-[36rem] overflow-hidden">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
            aria-hidden={i !== index}
            className={cn(
              "absolute inset-0 transition-opacity duration-700",
              i === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none",
            )}
          >
            <Image
              src={slide.image.url}
              alt={slide.image.altText}
              fill
              sizes="100vw"
              priority={i === 0}
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60" />
            <Container className="relative h-full flex items-center justify-center text-center">
              <div className="max-w-[44rem] text-white">
                <span className="inline-block text-xs uppercase tracking-[0.22em] font-medium text-white/85">
                  {slide.eyebrow}
                </span>
                <h1
                  className="mt-4 text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] leading-[1.05] text-white"
                  style={{
                    fontFamily: "var(--font-serif)",
                    fontWeight: 500,
                    letterSpacing: "-0.015em",
                    color: "#ffffff",
                  }}
                >
                  {slide.title}
                </h1>
                <p className="mt-5 text-base sm:text-lg text-white/85 max-w-[36rem] mx-auto">
                  {slide.description}
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <Link
                    href={slide.primaryCta.href}
                    className="inline-flex items-center justify-center h-12 px-6 rounded-[var(--radius-md)] bg-[color:var(--color-sale)] text-white text-sm font-semibold hover:bg-[color:var(--color-sale-dark)]"
                  >
                    {slide.primaryCta.label}
                  </Link>
                  <Link
                    href={slide.secondaryCta.href}
                    className="inline-flex items-center justify-center h-12 px-6 rounded-[var(--radius-md)] border border-white/50 bg-white/10 backdrop-blur-[2px] text-white text-sm font-semibold hover:bg-white/20"
                  >
                    {slide.secondaryCta.label}
                  </Link>
                </div>
              </div>
            </Container>
          </div>
        ))}

        <button
          type="button"
          aria-label="Previous slide"
          onClick={prev}
          className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/80 hover:bg-white text-[color:var(--color-sale-dark)] shadow-lift"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          type="button"
          aria-label="Next slide"
          onClick={next}
          className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/80 hover:bg-white text-[color:var(--color-sale-dark)] shadow-lift"
        >
          <ChevronRight size={22} />
        </button>

        <ol className="absolute bottom-5 left-0 right-0 z-20 flex items-center justify-center gap-2">
          {slides.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                onClick={() => go(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === index
                    ? "w-8 bg-white"
                    : "w-3 bg-white/50 hover:bg-white/80",
                )}
              />
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
